import type { ConexaoDoKommo } from "@base/contracts";
import {
  Button,
  CabecalhoDePagina,
  EstadoCarregando,
  EstadoDeErro,
  Etiqueta,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  cn,
} from "@base/ui";
import { Cable, CircleDashed, Construction, Database, Megaphone, PlugZap, Settings2 } from "lucide-react";
import { useState } from "react";
import { mensagemDoErro } from "../../../shared/http/mensagem-de-erro";
import { formatar } from "../../dashboard";
import { useConexaoGoogle, useConexaoKommo, useConexaoMeta } from "../application/conexoes.contexto";
import { CAMINHO_DO_GOOGLE } from "./pagina-do-google";
import { CAMINHO_DO_META } from "./pagina-do-meta";
import { CONEXOES, ROTULO_DA_SITUACAO, type Conexao, type IdDaConexao, type SituacaoDaConexao } from "../domain/catalogo";
import { FormularioDeConexao } from "./painel-do-kommo";
import { CAMINHO_DO_KOMMO } from "./pagina-do-kommo";
import { navegar } from "../../../shared/navegacao/navegacao";

const TOM_DA_SITUACAO: Readonly<Record<SituacaoDaConexao, "neutro" | "sucesso" | "atencao" | "perigo">> = {
  "nao-conectada": "neutro",
  conectada: "sucesso",
  sincronizando: "atencao",
  "com-erro": "perigo",
};

/**
 * Conexoes do projeto: Meta Ads, Google Ads e Kommo CRM.
 */
export function PaginaDeConexoes({ clienteId, cliente }: { readonly clienteId: string; readonly cliente: string }) {
  const kommo = useConexaoKommo(clienteId);
  const meta = useConexaoMeta(clienteId);
  const google = useConexaoGoogle(clienteId);
  const [aberta, setAberta] = useState<Conexao | null>(null);

  const cabecalho = (
    <CabecalhoDePagina
      rotulo={`${cliente} · Configurações`}
      titulo="Conexões"
      descricao="As fontes de dados do projeto. Cada conexão alimenta o Dashboard automaticamente."
    />
  );

  if (kommo.isPending) {
    return (
      <div className="flex flex-col gap-secao">
        {cabecalho}
        <EstadoCarregando rotulo="Carregando as conexões" />
      </div>
    );
  }
  if (kommo.isError) {
    return (
      <div className="flex flex-col gap-secao">
        {cabecalho}
        <EstadoDeErro mensagem={mensagemDoErro(kommo.error)} aoTentarNovamente={() => void kommo.refetch()} />
      </div>
    );
  }

  const conexaoKommo = kommo.data;
  const situacao = (id: IdDaConexao): SituacaoDaConexao =>
    id === "kommo"
      ? conexaoKommo.situacao
      : id === "meta-ads"
        ? (meta.data?.situacao ?? "nao-conectada")
        : id === "google-ads"
          ? (google.data?.situacao ?? "nao-conectada")
          : "nao-conectada";
  const conectadas = CONEXOES.filter((conexao) => situacao(conexao.id) !== "nao-conectada").length;

  return (
    <div className="flex flex-col gap-secao">
      {cabecalho}

      <ResumoDasConexoes conectadas={conectadas} total={CONEXOES.length} />

      <section aria-labelledby="titulo-fontes" className="flex flex-col gap-bloco">
        <div className="flex flex-col gap-1">
          <h2 id="titulo-fontes" className="text-xl font-normal tracking-tight">
            Fontes de dados
          </h2>
          <p className="text-sm text-muted-foreground">
            Mídia paga para investimento e leads do gerenciador; CRM para o restante do funil.
          </p>
        </div>
        <div className="grid gap-bloco lg:grid-cols-3">
          {CONEXOES.map((conexao) => (
            <CartaoDeConexao
              key={conexao.id}
              conexao={conexao}
              situacao={situacao(conexao.id)}
              ultimaSincronizacaoEm={
                conexao.id === "kommo"
                  ? conexaoKommo.ultimaSincronizacaoEm
                  : conexao.id === "meta-ads"
                    ? (meta.data?.ultimaSincronizacaoEm ?? null)
                    : conexao.id === "google-ads"
                      ? (google.data?.ultimaSincronizacaoEm ?? null)
                      : null
              }
              aoAbrir={() =>
                conexao.id === "meta-ads"
                  ? navegar(CAMINHO_DO_META)
                  : conexao.id === "google-ads"
                    ? navegar(CAMINHO_DO_GOOGLE)
                  : conexao.id === "kommo" && conexaoKommo.situacao !== "nao-conectada"
                    ? navegar(CAMINHO_DO_KOMMO)
                    : setAberta(conexao)
              }
            />
          ))}
        </div>
      </section>

      <Dialog open={aberta !== null} onOpenChange={(abrir) => (abrir ? undefined : setAberta(null))}>
        <DialogContent tamanho="largo">
          <DialogHeader rotulo="Conexões">
            <DialogTitle>{aberta?.nome}</DialogTitle>
            <DialogDescription>Conectar {aberta?.nome}, passo a passo.</DialogDescription>
          </DialogHeader>
          {aberta?.id === "kommo" ? (
            <FormularioDeConexao
              clienteId={clienteId}
              subdominioAtual={null}
              // Conectou: segue para a tela do Kommo, na escolha das etapas.
              aoConcluir={() => navegar(`${CAMINHO_DO_KOMMO}?aba=funil`)}
            />
          ) : aberta ? (
            <PainelEmConstrucao conexao={aberta} />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PainelEmConstrucao({ conexao }: { readonly conexao: Conexao }) {
  return (
    <div className="flex flex-col gap-bloco">
      <ol className="flex flex-col gap-4">
        {conexao.passos.map((passo, indice) => (
          <li key={passo} className="flex items-start gap-3 text-sm leading-relaxed">
            <span className="grid size-6 shrink-0 place-items-center rounded-full bg-accent text-xs font-semibold text-accent-foreground">
              {indice + 1}
            </span>
            {passo}
          </li>
        ))}
      </ol>
      <p className="flex items-start gap-3 rounded-2xl bg-warning/10 px-4 py-3 text-sm leading-relaxed">
        <Construction className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden="true" />
        A integração com {conexao.nome} ainda está em construção. Enquanto isso, investimento e leads do
        gerenciador ficam em branco no Dashboard.
      </p>
      <Button disabled>Conectar {conexao.nome}</Button>
    </div>
  );
}

function ResumoDasConexoes({ conectadas, total }: { readonly conectadas: number; readonly total: number }) {
  return (
    <div className="flex flex-col gap-6 rounded-3xl bg-secondary p-6 text-secondary-foreground md:flex-row md:items-center md:justify-between md:p-8">
      <div className="flex items-center gap-4">
        <span className="grid size-12 shrink-0 place-items-center rounded-full bg-white/10 text-ice">
          <Cable className="size-5" aria-hidden="true" />
        </span>
        <div className="flex flex-col gap-1">
          <p className="text-2xl font-medium tracking-tight">
            {conectadas} de {total} fontes conectadas
          </p>
          <p className="text-sm text-secondary-foreground/75">
            {conectadas === total
              ? "Tudo pronto: o Dashboard usa os dados reais do projeto."
              : "O que vier de fonte desconectada aparece zerado no Dashboard."}
          </p>
        </div>
      </div>
      <div className="flex gap-2" aria-hidden="true">
        {Array.from({ length: total }, (_, indice) => (
          <span
            key={indice}
            className={cn("h-2 w-10 rounded-full", indice < conectadas ? "bg-ice" : "bg-white/15")}
          />
        ))}
      </div>
    </div>
  );
}

function CartaoDeConexao({
  conexao,
  situacao,
  ultimaSincronizacaoEm,
  aoAbrir,
}: {
  readonly conexao: Conexao;
  readonly situacao: SituacaoDaConexao;
  readonly ultimaSincronizacaoEm: ConexaoDoKommo["ultimaSincronizacaoEm"];
  readonly aoAbrir: () => void;
}) {
  const Icone = conexao.categoria === "CRM" ? Database : Megaphone;
  const conectada = situacao !== "nao-conectada";
  return (
    <article className="flex flex-col gap-6 rounded-3xl bg-card p-6 shadow-sm ring-1 ring-border/70">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="grid size-11 place-items-center rounded-2xl bg-accent text-accent-foreground">
            <Icone className="size-5" aria-hidden="true" />
          </span>
          <div className="flex flex-col">
            <h3 className="text-base font-semibold">{conexao.nome}</h3>
            <span className="text-xs text-muted-foreground">{conexao.categoria}</span>
          </div>
        </div>
        <Etiqueta tom={TOM_DA_SITUACAO[situacao]}>{ROTULO_DA_SITUACAO[situacao]}</Etiqueta>
      </div>

      <p className="text-sm text-muted-foreground">{conexao.descricao}</p>

      <div className="flex flex-col gap-3">
        <p className="text-xs font-semibold tracking-[0.12em] text-muted-foreground uppercase">Alimenta</p>
        <ul className="flex flex-col gap-2">
          {conexao.alimenta.map((item) => (
            <li key={item} className="flex items-start gap-2 text-sm">
              <CircleDashed className="mt-0.5 size-3.5 shrink-0 text-primary" aria-hidden="true" />
              {item}
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-auto flex items-center justify-between gap-3 border-t pt-5">
        <span className="text-xs text-muted-foreground">
          {ultimaSincronizacaoEm ? `Sincronizado em ${formatar.momento(ultimaSincronizacaoEm)}` : "Nunca sincronizado"}
        </span>
        <Button variant={conectada ? "outline" : "default"} onClick={aoAbrir}>
          {conectada ? <Settings2 aria-hidden="true" /> : <PlugZap aria-hidden="true" />}
          {conectada ? "Configurar" : "Conectar"}
        </Button>
      </div>
    </article>
  );
}
